import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import {
  selectHighlightedIssue,
  selectHighlightSource
} from 'app/pages/Home/slice/selectors';
import { buildEventHighlightSet } from './buildEventHighlightSet';
import type { MonthlyTimelineItem } from './types';
import { isEventLane } from './monthlyItemIconUtils';

let monthlyEventHighlightSourceKey: string | null = null;

function getMonthlyEventHighlightKey(item: MonthlyTimelineItem): string {
  const issueId = item.issueId || '';
  const releasePhaseId = item.releasePhaseId;
  const releasePhase = item.releasePhase;
  const actualShowOnTimeline = Boolean(item.showOnTimeline);

  if (releasePhaseId) {
    return `${issueId}-${releasePhaseId}-${actualShowOnTimeline}`;
  }

  if (releasePhase) {
    return `${issueId}-${releasePhase}-${actualShowOnTimeline}`;
  }

  return issueId;
}

export function resetMonthlyEventHighlightSourceKey(): void {
  monthlyEventHighlightSourceKey = null;
}

export function isMonthlyEventHighlightSource(
  item: MonthlyTimelineItem
): boolean {
  if (!monthlyEventHighlightSourceKey) {
    return false;
  }

  const itemKey = getMonthlyEventHighlightKey(item);
  const issueId = item.issueId || '';

  return (
    monthlyEventHighlightSourceKey === itemKey ||
    monthlyEventHighlightSourceKey === issueId
  );
}

export function isMonthlyEventItem(
  item: MonthlyTimelineItem,
  laneLabel: string
): boolean {
  const type = item.type || item.projectType || '';
  return isEventLane(laneLabel) || type === 'Events' || type.includes('Events');
}

export function useMonthlyEventHighlight(
  item: MonthlyTimelineItem,
  laneLabel: string
) {
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();
  const highlightedIssue = useSelector(selectHighlightedIssue);
  const highlightSource = useSelector(selectHighlightSource);

  const issueId = item.issueId || '';
  const releasePhaseId = item.releasePhaseId;
  const releasePhase = item.releasePhase;
  const actualShowOnTimeline = Boolean(item.showOnTimeline);

  const compositeKeyWithId = releasePhaseId
    ? `${issueId}-${releasePhaseId}-${actualShowOnTimeline}`
    : null;
  const compositeKeyWithName = releasePhase
    ? `${issueId}-${releasePhase}-${actualShowOnTimeline}`
    : null;

  const isHighlighted =
    !!highlightedIssue[issueId] ||
    (compositeKeyWithId && !!highlightedIssue[compositeKeyWithId]) ||
    (compositeKeyWithName && !!highlightedIssue[compositeKeyWithName]);

  const highLightCards = useCallback(() => {
    if (!isMonthlyEventItem(item, laneLabel)) return;

    if (!isHighlighted) {
      dispatch(actions.clearHighlightedIssues());
      monthlyEventHighlightSourceKey = getMonthlyEventHighlightKey(item);

      const { highlightSet, parentToUnnest } = buildEventHighlightSet(
        item,
        'single-click'
      );

      dispatch(
        actions.updateHighlightedIssue({
          relatedIssues: Array.from(highlightSet),
          unnestParentIds: Array.from(parentToUnnest),
          source: 'single-click'
        })
      );
    } else {
      dispatch(actions.clearHighlightedIssues());
      monthlyEventHighlightSourceKey = null;
    }
  }, [actions, dispatch, isHighlighted, item, laneLabel]);

  const showHighlight =
    isHighlighted &&
    (highlightSource === 'filter' ||
      highlightSource === 'search' ||
      !isMonthlyEventHighlightSource(item));

  const isHighlightSource = isHighlighted && isMonthlyEventHighlightSource(item);

  const parsedBadgeCount = parseInt(item.relationship?.badgecount ?? '', 10);
  const implementedCount = Number.isNaN(parsedBadgeCount) ? 0 : parsedBadgeCount;

  return {
    highLightCards,
    isHighlighted,
    showHighlight,
    isHighlightSource,
    implementedCount
  };
}
